import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cardCls, fmtDuration } from "./ui";

const RANGES = [["today", "Today"], ["week", "This Week"], ["month", "This Month"]];

const StatCard = ({ label, value, sub }) => (
  <div className={`${cardCls} p-6`} data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-midnight/40">{label}</p>
    <p className="font-heading text-3xl font-light text-midnight mt-2">{value}</p>
    {sub && <p className="text-xs text-midnight/45 mt-1">{sub}</p>}
  </div>
);

const Bar = ({ label, value, max, gold }) => (
  <div className="mb-3">
    <div className="flex justify-between text-xs mb-1">
      <span className="text-midnight/60">{label}</span>
      <span className="font-medium text-midnight">{value}</span>
    </div>
    <div className="h-1.5 bg-midnight/[0.06]">
      <div
        className={gold ? "h-full bg-gold" : "h-full bg-midnight"}
        style={{ width: `${max ? Math.max(2, (value / max) * 100) : 0}%` }}
      />
    </div>
  </div>
);

const CONVERSION_LABELS = {
  rfq_submitted: "RFQs Submitted",
  registration_completed: "Client Registrations",
  marketing_subscribed: "Subscriptions",
  ai_started: "AI Conversations",
  ai_summary_generated: "AI Requirements",
  availability_requested: "Availability Requests",
  contact_submitted: "Contact Enquiries",
  document_uploaded: "Document Uploads",
};

export const DashboardSection = () => {
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/overview", { params: { range } }).then((r) => setData(r.data)).catch(() => {});
  }, [range]);

  return (
    <div data-testid="admin-dashboard-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-medium text-midnight">Traffic Overview</h2>
        <div className="flex gap-1" data-testid="analytics-range">
          {RANGES.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setRange(id)}
              data-testid={`range-${id}`}
              className={`px-4 py-2 text-xs font-medium transition-colors ${range === id ? "bg-midnight text-white" : "bg-white text-midnight/55 hairline hover:border-gold"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-midnight/40 animate-pulse">Loading analytics…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <StatCard label="Visitors" value={data.visitors} />
            <StatCard label="Page Views" value={data.page_views} />
            <StatCard label="Avg Session" value={fmtDuration(data.avg_session_seconds)} />
            <StatCard label="Engagement" value={`${data.engagement_rate}%`} />
            <StatCard label="New" value={data.new_visitors} />
            <StatCard label="Returning" value={data.returning_visitors} />
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <div className={`${cardCls} p-6`} data-testid="funnel-card">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-5">Conversion Funnel</p>
              {[
                ["Visitors", data.funnel.visitors],
                ["Product / category viewed", data.funnel.product_viewed],
                ["Requirement started", data.funnel.requirement_started],
                ["RFQ submitted", data.funnel.rfq_submitted],
                ["Client registered", data.funnel.registered],
              ].map(([label, v], i) => (
                <Bar key={label} label={label} value={v} max={data.funnel.visitors || 1} gold={i >= 3} />
              ))}
              <div className="mt-6 pt-5 border-t border-midnight/10">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-4">Royal AI Funnel</p>
                <div className="flex flex-wrap gap-6 text-sm">
                  <span className="text-midnight/70">Started <span className="font-semibold text-midnight">{data.ai_funnel.ai_started}</span></span>
                  <span className="text-midnight/40">→</span>
                  <span className="text-midnight/70">Structured <span className="font-semibold text-midnight">{data.ai_funnel.ai_structured}</span></span>
                  <span className="text-midnight/40">→</span>
                  <span className="text-midnight/70">RFQs from AI <span className="font-semibold text-gold">{data.ai_funnel.ai_rfq}</span></span>
                </div>
              </div>
            </div>

            <div className={`${cardCls} p-6`} data-testid="conversions-card">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-5">Conversion Activity</p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(CONVERSION_LABELS).map(([k, label]) => (
                  <div key={k} className="border border-midnight/[0.08] px-4 py-3">
                    <p className="font-heading text-xl font-light text-midnight">{data.conversions[k] ?? 0}</p>
                    <p className="text-[11px] text-midnight/50 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const TrafficSection = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/overview", { params: { range: "month" } }).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm text-midnight/40 animate-pulse">Loading traffic…</p>;

  const List = ({ title, items, testId }) => (
    <div className={`${cardCls} p-6`} data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-5">{title}</p>
      {items.length === 0 && <p className="text-sm text-midnight/40">No data yet.</p>}
      {items.map((i) => (
        <Bar key={i.label} label={i.label || "—"} value={i.count} max={items[0]?.count || 1} />
      ))}
    </div>
  );

  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-traffic-section">
      <List title="Acquisition" items={data.acquisition} testId="traffic-acquisition" />
      <List title="Geography" items={data.geo} testId="traffic-geo" />
      <List title="Devices" items={data.devices} testId="traffic-devices" />
      <List title="Browsers" items={data.browsers} testId="traffic-browsers" />
      <List title="Operating Systems" items={data.os} testId="traffic-os" />
      <List title="Most Viewed Pages" items={data.top_pages} testId="traffic-pages" />
    </div>
  );
};
